import { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';

import { getCookie } from 'src/utils/cookie';
import { clearAllUtmData } from 'src/utils/utm';
import useUTMStore from './stores/useUTMStore';

/**
 * Hook para gerenciar parâmetros UTM em toda a aplicação
 * 
 * 1. Verifica a URL atual para parâmetros UTM
 * 2. Sincroniza com cookies existentes
 * 3. Mantém os dados em localStorage via Zustand
 * 4. Fornece métodos para acessar e manipular UTMs
 * 5. Preserva a primeira UTM capturada por 90 dias
 * 
 * Regras de negócio:
 * - A propagação de UTM só é ativada quando o usuário entra com UTM na landing page
 * - Os parâmetros UTM só são salvos na landing page (/solucoes) ou na página de criar evento (me/events/new/what)
 * - Os parâmetros UTM só aparecem na URL quando o usuário está na landing page ou na página de criar evento
 * - Em outras páginas, os parâmetros UTM são propagados mas não aparecem na URL
 */

// Flag para indicar que o usuário já passou por uma página permitida nesta sessão
const PASSED_ALLOWED_PAGE_KEY = 'uticket-passed-allowed-page';

export const useUtm = () => {
    const location = useLocation();
    const lastSyncTime = useRef<number>(0);
    const { 
        utm, 
        setUTM, 
        getUTM, 
        clearUTM, 
        syncFromCookie, 
        shouldPropagateUTM,
        enableUTMPropagation,
        disableUTMPropagation,
        getSessionUTM,
        setSessionUTM,
        clearSessionUTM,
        hasSessionUTM
    } = useUTMStore();

    const isAllowedPage = useCallback((): boolean => {
        const isLandingPage = location.pathname === '/solucoes' || 
                                location.pathname.startsWith('/solucoes/');

        const isCreateEventPage = location.pathname === '/me/events/new/what'
        return isLandingPage || isCreateEventPage;
    }, [location.pathname]);

    const hasValidUtm = useCallback((): boolean => {
        const utmData = getUTM();
        return !!utmData;
    }, [getUTM]);

    const debouncedSyncFromCookie = useCallback(() => {
        const now = Date.now();
        if (now - lastSyncTime.current > 1000) {
            syncFromCookie();
            lastSyncTime.current = now;
        }
    }, [syncFromCookie]);

    useEffect(() => {
        const searchParams = new URLSearchParams(location.search);
        const hasUtmInUrl = Array.from(searchParams.keys()).some(key => key.startsWith('utm_'));
        const hasPassedAllowedPage = sessionStorage.getItem(PASSED_ALLOWED_PAGE_KEY) === 'true';

        if (hasUtmInUrl) {
            if (isAllowedPage()) {
                sessionStorage.setItem(PASSED_ALLOWED_PAGE_KEY, 'true');
                setUTM(location.search.substring(1));
                enableUTMPropagation();
                clearSessionUTM();
            } else if (!hasPassedAllowedPage) {
                setSessionUTM(location.search.substring(1));
                enableUTMPropagation();
            }
        } else if (!hasUtmInUrl) {
            debouncedSyncFromCookie();

            if (!hasValidUtm() && !hasSessionUTM()) {
                localStorage.removeItem('uticket-utm-storage');
            }
        }
    }, [location.search, setUTM, debouncedSyncFromCookie, enableUTMPropagation, isAllowedPage, hasValidUtm, setSessionUTM, clearSessionUTM, hasSessionUTM]);

    useEffect(() => {
        if (isAllowedPage()) {
            sessionStorage.setItem(PASSED_ALLOWED_PAGE_KEY, 'true');
            debouncedSyncFromCookie();
        
            const sessionUtm = getSessionUTM();
            if (sessionUtm) {
                setUTM(sessionUtm);
                clearSessionUTM();
            }
        }
    }, [debouncedSyncFromCookie, isAllowedPage, getSessionUTM, setUTM, clearSessionUTM]);

    useEffect(() => {
        const handlePageReload = () => {
            debouncedSyncFromCookie();
        };

        window.addEventListener('beforeunload', handlePageReload);
        if (performance.navigation && performance.navigation.type === 1) {
            debouncedSyncFromCookie();
        }
        return () => {
            window.removeEventListener('beforeunload', handlePageReload);
        };
    }, [debouncedSyncFromCookie]);

    /**
     * Obtém os parâmetros UTM para envio ao backend
     * Prioriza dados persistentes: localStorage (Zustand) e depois cookies
     * NÃO utiliza dados temporários de sessão
     * NÃO depende do estado de propagação (shouldPropagateUTM)
     */
    const getUtmWithFallback = (): string | null => {
        // 1. Verificar no estado do Zustand
        const storeUtm = getUTM();
        if (storeUtm) {
            return storeUtm;
        }
        
        // 2. Verificar nos cookies como fallback
        const cookieUtm = getCookie('uticketUtm');
        if (cookieUtm) {
            debouncedSyncFromCookie();
            return getUTM();
        }
        
        return null;
    };

    /**
     * Obtém os parâmetros UTM para propagação entre páginas
     * Prioriza dados temporários de sessão, depois dados persistentes
     * Respeita o estado de propagação (shouldPropagateUTM)
     */
    const getUtmForPropagation = (): string | null => {
        if (!shouldPropagateUTM()) {
            return null;
        }
        
        // 1. Verificar UTM temporária na sessão
        const sessionUtm = getSessionUTM();
        if (sessionUtm) {
            return sessionUtm;
        }
        
        // 2. Verificar no estado do Zustand
        const storeUtm = getUTM();
        if (storeUtm) {
            return storeUtm;
        }
        
        // 3. Verificar nos cookies como fallback
        const cookieUtm = getCookie('uticketUtm');
        if (cookieUtm && isAllowedPage()) {
            debouncedSyncFromCookie();
            return getUTM();
        }
        
        return null;
    };

    /**
     * Verifica se os parâmetros UTM devem ser exibidos na URL
     * 
     * @returns true se os parâmetros UTM devem ser exibidos na URL, false caso contrário
     */
    const shouldShowUtmInUrl = (): boolean => {
        return isAllowedPage() || (hasValidUtm() && !hasSessionUTM());
    };
    
    /**
     * Limpa completamente todos os dados de UTM do navegador
     * Útil para testes e depuração
     * 
     * @returns Objeto indicando o que foi limpo ou objeto vazio se não for ambiente permitido
     */
    const clearAllUtm = useCallback((): {localStorage: boolean, cookie: boolean, sessionStorage: boolean} => {
        clearUTM();
        clearSessionUTM();
        sessionStorage.removeItem(PASSED_ALLOWED_PAGE_KEY);
        return clearAllUtmData() || { localStorage: false, cookie: false, sessionStorage: false };
    }, [clearUTM, clearSessionUTM]);
    
    return {
        utm: utm,
        utmString: utm.utmParams,
        hasUtm: hasValidUtm(),
        getUtmWithFallback,
        getUtmForPropagation,
        setUTM,
        clearUTM,
        clearAllUtm,
        shouldPropagateUTM,
        enableUTMPropagation,
        disableUTMPropagation,
        isAllowedPage,
        hasValidUtm, 
        shouldShowUtmInUrl,
        hasSessionUTM,
        getSessionUTM,
        setSessionUTM,
        clearSessionUTM
    };
};

export default useUtm; 